/**
 * Codec design pattern : to securely read and write filters to and from the URL
 *
 * Conventions:
 * - `read` returns `undefined` when the raw value is absent or invalid, so the
 *   default value is kept. Always validate: a hand-edited query string must not
 *   be able to push a bogus value into the state.
 * - `write` returns `null` when the field sits at its default value, so it stays
 *   out of the URL and the query string stays readable.
 */
export type UrlCodec<T> = {
  param: string;
  read: (raw: string) => T | undefined;
  write: (value: T) => string | null;
};

export type UrlFilterCodecs<S> = { [K in keyof S]: UrlCodec<S[K]> };

const DAY_SEPARATOR = ",";

/**
 * Filter dates are local days: serializing them as UTC ISO strings would shift a
 * shared link by one day depending on the reader's timezone.
 */
export const toDayParam = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

export const parseDayParam = (raw: string): Date | undefined => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (!match) return undefined;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? undefined : date;
};

const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);

const endOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

export const stringCodec = (param: string): UrlCodec<string> => ({
  param,
  read: (raw) => raw,
  write: (value) => value || null,
});

export const nullableStringCodec = (
  param: string,
  allowedValues?: readonly string[],
): UrlCodec<string | null> => ({
  param,
  read: (raw) => {
    if (!raw) return undefined;
    if (allowedValues && !allowedValues.includes(raw)) return undefined;
    return raw;
  },
  write: (value) => value || null,
});

export const booleanCodec = (param: string): UrlCodec<boolean> => ({
  param,
  read: (raw) => (raw === "true" ? true : raw === "false" ? false : undefined),
  write: (value) => (value ? "true" : null),
});

export const nullableBooleanCodec = (param: string): UrlCodec<boolean | null> => ({
  param,
  read: (raw) => (raw === "true" ? true : raw === "false" ? false : undefined),
  write: (value) => (value === null ? null : String(value)),
});

export const enumCodec = <T extends string>(
  param: string,
  values: readonly T[],
  defaultValue: T,
): UrlCodec<T> => ({
  param,
  read: (raw) => (values.includes(raw as T) ? (raw as T) : undefined),
  write: (value) => (value === defaultValue ? null : value),
});

export const nullableEnumCodec = <T extends string>(
  param: string,
  values: readonly T[],
): UrlCodec<T | null> => ({
  param,
  read: (raw) => (values.includes(raw as T) ? (raw as T) : undefined),
  // `value ||` and not `value ??`: some legacy call sites clear the filter with "".
  write: (value) => value || null,
});

export const dayCodec = (
  param: string,
  options: { normalize?: (date: Date) => Date; isDefault?: (date: Date) => boolean } = {},
): UrlCodec<Date> => {
  const normalize = options.normalize ?? ((date: Date) => date);
  return {
    param,
    read: (raw) => {
      const date = parseDayParam(raw);
      return date ? normalize(date) : undefined;
    },
    write: (value) => (options.isDefault?.(value) ? null : toDayParam(value)),
  };
};

export const dayListCodec = (param: string): UrlCodec<Date[]> => ({
  param,
  read: (raw) => {
    const days = raw
      .split(DAY_SEPARATOR)
      .map(parseDayParam)
      .filter((day): day is Date => day !== undefined);
    return days.length > 0 ? days : undefined;
  },
  write: (value) => (value.length > 0 ? value.map(toDayParam).join(DAY_SEPARATOR) : null),
});

export const dayRangeCodec = (param: string): UrlCodec<[Date, Date] | null> => ({
  param,
  read: (raw) => {
    const [from, to] = raw.split(DAY_SEPARATOR).map(parseDayParam);
    return from && to ? [startOfDay(from), endOfDay(to)] : undefined;
  },
  write: (value) => (value ? value.map(toDayParam).join(DAY_SEPARATOR) : null),
});

function readFilter<S extends object, K extends keyof S>(
  key: K,
  codecs: UrlFilterCodecs<S>,
  searchParams: URLSearchParams,
  target: S,
): void {
  const codec = codecs[key];
  const raw = searchParams.get(codec.param);
  if (raw === null) return;
  const value = codec.read(raw);
  if (value !== undefined) target[key] = value;
}

function writeFilter<S extends object, K extends keyof S>(
  key: K,
  codecs: UrlFilterCodecs<S>,
  filters: S,
  searchParams: URLSearchParams,
): void {
  const codec = codecs[key];
  const raw = codec.write(filters[key]);
  if (raw === null) searchParams.delete(codec.param);
  else searchParams.set(codec.param, raw);
}

export function readFiltersFromUrl<S extends object>(
  searchParams: URLSearchParams,
  defaults: S,
  codecs: UrlFilterCodecs<S>,
): S {
  const state = { ...defaults };
  (Object.keys(codecs) as (keyof S)[]).forEach((key) =>
    readFilter(key, codecs, searchParams, state),
  );
  return state;
}

export function buildFiltersQuery<S extends object>(
  filters: S,
  codecs: UrlFilterCodecs<S>,
): string {
  const params = new URLSearchParams();
  (Object.keys(codecs) as (keyof S)[]).forEach((key) => writeFilter(key, codecs, filters, params));
  return params.toString();
}

export function getFilterParamNames<S extends object>(codecs: UrlFilterCodecs<S>): string[] {
  return Object.values(codecs as unknown as Record<string, { param: string }>).map(
    (codec) => codec.param,
  );
}
